import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  getDealerVisits, getDealerVisitStats, createDealerVisit, updateDealerVisit,
  deleteDealerVisit, getDealers, getLocations, visitCheckIn, visitCheckOut,
  addVisitPhoto, uploadFile, getRepslySettings, updateRepslySettings,
  triggerRepslySync, testRepslyConnection,
} from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  MapPin, Calendar, Plus, Trash2, Loader2, Search, Clock, CheckCircle,
  XCircle, Eye, Users, Camera, ClipboardList, ArrowRight, ArrowLeft, BarChart3,
  AlertTriangle, TrendingUp, ImageIcon, ChevronDown, Play, Square,
  FileText, X, Edit, Settings, RefreshCw, Wifi, WifiOff,
} from "lucide-react";

const STATUS_CONFIG = {
  scheduled: { label: "Scheduled", color: "bg-blue-100 text-blue-700", icon: Calendar },
  "in-progress": { label: "In Progress", color: "bg-amber-100 text-amber-700", icon: Play },
  completed: { label: "Completed", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  missed: { label: "Missed", color: "bg-red-100 text-red-700", icon: XCircle },
};

const VISIT_TYPES = [
  { value: "routine", label: "Routine Check" },
  { value: "audit", label: "Store Audit" },
  { value: "training", label: "Training Visit" },
  { value: "setup", label: "New Store Setup" },
  { value: "support", label: "Support Visit" },
  { value: "inventory", label: "Inventory Check" },
];

function StatCard({ label, value, icon: Icon, color, sub }) {
  return (
    <Card className="border-slate-200">
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-navy font-heading">{value}</p>
          <p className="text-xs text-slate-500 font-body">{label}</p>
          {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function CreateVisitDialog({ dealers, locations, onCreated }) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [dealerSearch, setDealerSearch] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [form, setForm] = useState({
    dealer_id: "", location_id: "", scheduled_date: "",
    visit_type: "routine", notes: "", tasks: [{ description: "" }],
  });

  const filteredDealers = dealers.filter((d) =>
    `${d.first_name} ${d.last_name}`.toLowerCase().includes(dealerSearch.toLowerCase()) ||
    d.email?.toLowerCase().includes(dealerSearch.toLowerCase())
  );

  const filteredLocations = locations.filter((l) =>
    l.name.toLowerCase().includes(locationSearch.toLowerCase()) ||
    l.address?.toLowerCase().includes(locationSearch.toLowerCase()) ||
    l.tsp_id?.toLowerCase().includes(locationSearch.toLowerCase())
  );

  const selectedDealer = dealers.find((d) => d.id === form.dealer_id);
  const selectedLocation = locations.find((l) => l.id === form.location_id);

  const addTask = () => setForm({ ...form, tasks: [...form.tasks, { description: "" }] });
  const removeTask = (i) => setForm({ ...form, tasks: form.tasks.filter((_, idx) => idx !== i) });
  const updateTask = (i, val) => {
    const tasks = [...form.tasks];
    tasks[i] = { description: val };
    setForm({ ...form, tasks });
  };

  const handleCreate = async () => {
    if (!form.dealer_id || !form.location_id || !form.scheduled_date) {
      toast.error("Please fill dealer, location, and date");
      return;
    }
    setCreating(true);
    try {
      const tasks = form.tasks.filter((t) => t.description.trim());
      await createDealerVisit({ ...form, tasks });
      toast.success("Visit scheduled!");
      setOpen(false);
      setForm({ dealer_id: "", location_id: "", scheduled_date: "", visit_type: "routine", notes: "", tasks: [{ description: "" }] });
      setDealerSearch("");
      setLocationSearch("");
      onCreated();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to create visit"); }
    finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-teal hover:bg-teal/90 text-white" data-testid="schedule-visit-btn">
          <Plus className="w-4 h-4 mr-2" /> Schedule Visit
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-visit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-navy">Schedule New Visit</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {/* Dealer selection */}
          <div>
            <Label>Dealer *</Label>
            {selectedDealer ? (
              <div className="flex items-center gap-2 p-2 bg-teal/5 border border-teal/20 rounded-lg mt-1">
                <Users className="w-4 h-4 text-teal" />
                <span className="text-sm font-medium">{selectedDealer.first_name} {selectedDealer.last_name}</span>
                <span className="text-xs text-slate-400">{selectedDealer.email}</span>
                <button className="ml-auto text-slate-400 hover:text-red-500" onClick={() => setForm({ ...form, dealer_id: "" })}><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="mt-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input value={dealerSearch} onChange={(e) => setDealerSearch(e.target.value)} placeholder="Search dealer by name or email..." className="pl-10" data-testid="visit-dealer-search" />
                </div>
                {dealerSearch && (
                  <div className="mt-1 max-h-32 overflow-y-auto border border-slate-200 rounded-lg divide-y">
                    {filteredDealers.slice(0, 10).map((d) => (
                      <div key={d.id} className="flex items-center gap-2 p-2 hover:bg-slate-50 cursor-pointer text-sm" onClick={() => { setForm({ ...form, dealer_id: d.id }); setDealerSearch(""); }} data-testid={`select-dealer-${d.id}`}>
                        <Users className="w-3.5 h-3.5 text-slate-400" />
                        <span className="font-medium">{d.first_name} {d.last_name}</span>
                        <span className="text-xs text-slate-400 ml-auto">{d.email}</span>
                      </div>
                    ))}
                    {filteredDealers.length === 0 && <p className="text-xs text-slate-400 p-2 text-center">No dealers found</p>}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Location selection */}
          <div>
            <Label>Location *</Label>
            {selectedLocation ? (
              <div className="flex items-center gap-2 p-2 bg-teal/5 border border-teal/20 rounded-lg mt-1">
                <MapPin className="w-4 h-4 text-teal" />
                <span className="text-sm font-medium">{selectedLocation.name}</span>
                <span className="text-xs text-slate-400">{selectedLocation.address}, {selectedLocation.city}</span>
                <button className="ml-auto text-slate-400 hover:text-red-500" onClick={() => setForm({ ...form, location_id: "" })}><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="mt-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input value={locationSearch} onChange={(e) => setLocationSearch(e.target.value)} placeholder="Search by name, address, or TSP ID..." className="pl-10" data-testid="visit-location-search" />
                </div>
                {locationSearch && (
                  <div className="mt-1 max-h-32 overflow-y-auto border border-slate-200 rounded-lg divide-y">
                    {filteredLocations.slice(0, 10).map((l) => (
                      <div key={l.id} className="flex items-center gap-2 p-2 hover:bg-slate-50 cursor-pointer text-sm" onClick={() => { setForm({ ...form, location_id: l.id }); setLocationSearch(""); }} data-testid={`select-location-${l.id}`}>
                        <MapPin className="w-3.5 h-3.5 text-slate-400" />
                        <span className="font-medium">{l.name}</span>
                        <span className="text-xs text-slate-400 ml-auto">{l.tsp_id || ""}</span>
                      </div>
                    ))}
                    {filteredLocations.length === 0 && <p className="text-xs text-slate-400 p-2 text-center">No locations found</p>}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Scheduled Date *</Label>
              <Input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} data-testid="visit-date" />
            </div>
            <div>
              <Label>Visit Type</Label>
              <select value={form.visit_type} onChange={(e) => setForm({ ...form, visit_type: e.target.value })} className="w-full mt-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="visit-type">
                {VISIT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>

          {/* Tasks */}
          <div>
            <div className="flex items-center justify-between">
              <Label>Tasks / Checklist</Label>
              <button onClick={addTask} className="text-xs text-teal hover:underline">+ Add Task</button>
            </div>
            <div className="space-y-2 mt-1">
              {form.tasks.map((task, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={task.description} onChange={(e) => updateTask(i, e.target.value)} placeholder={`Task ${i + 1}`} className="flex-1" data-testid={`visit-task-${i}`} />
                  {form.tasks.length > 1 && (
                    <button onClick={() => removeTask(i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Additional notes for this visit..." data-testid="visit-notes" />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-teal hover:bg-teal/90 text-white" data-testid="create-visit-submit">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />} Schedule Visit
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VisitDetailDialog({ visit, open, onClose, onUpdate }) {
  const [adminNotes, setAdminNotes] = useState(visit?.admin_notes || "");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(visit?.status || "scheduled");

  useEffect(() => {
    if (visit) {
      setAdminNotes(visit.admin_notes || "");
      setStatus(visit.status || "scheduled");
    }
  }, [visit]);

  if (!visit) return null;
  const cfg = STATUS_CONFIG[visit.status] || STATUS_CONFIG.scheduled;
  const StatusIcon = cfg.icon;
  const visitType = VISIT_TYPES.find((t) => t.value === visit.visit_type);
  const completedTasks = (visit.tasks || []).filter((t) => t.completed).length;
  const totalTasks = (visit.tasks || []).length;

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDealerVisit(visit.id, { admin_notes: adminNotes, status });
      toast.success("Visit updated");
      onUpdate();
    } catch (err) { toast.error("Failed to update"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="visit-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-navy">Visit Details</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Header info */}
          <div className="flex items-start justify-between gap-4 p-4 bg-slate-50 rounded-lg">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className={`px-2 py-0.5 text-xs rounded font-medium inline-flex items-center gap-1 ${cfg.color}`}>
                  <StatusIcon className="w-3 h-3" /> {cfg.label}
                </span>
                <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">{visitType?.label || visit.visit_type}</span>
              </div>
              <p className="font-heading font-bold text-navy">{visit.location_name}</p>
              <p className="text-xs text-slate-500">{visit.location_address}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-navy">{visit.dealer_name}</p>
              <p className="text-xs text-slate-400">{visit.dealer_email}</p>
            </div>
          </div>

          {/* Times */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <Calendar className="w-4 h-4 text-blue-500 mx-auto mb-1" />
              <p className="text-xs text-slate-500">Scheduled</p>
              <p className="text-sm font-medium text-navy">{visit.scheduled_date}</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg text-center">
              <Play className="w-4 h-4 text-amber-500 mx-auto mb-1" />
              <p className="text-xs text-slate-500">Check In</p>
              <p className="text-sm font-medium text-navy">{visit.check_in_time ? new Date(visit.check_in_time).toLocaleString() : "—"}</p>
            </div>
            <div className="p-3 bg-emerald-50 rounded-lg text-center">
              <Square className="w-4 h-4 text-emerald-500 mx-auto mb-1" />
              <p className="text-xs text-slate-500">Check Out</p>
              <p className="text-sm font-medium text-navy">{visit.check_out_time ? new Date(visit.check_out_time).toLocaleString() : "—"}</p>
            </div>
          </div>

          {/* Tasks */}
          {totalTasks > 0 && (
            <div>
              <p className="text-sm font-medium text-navy mb-2">Tasks ({completedTasks}/{totalTasks})</p>
              <div className="space-y-1.5">
                {visit.tasks.map((t, i) => (
                  <div key={i} className={`flex items-center gap-2 p-2 rounded-lg text-sm ${t.completed ? "bg-emerald-50" : "bg-slate-50"}`}>
                    {t.completed ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <div className="w-4 h-4 rounded-full border-2 border-slate-300" />}
                    <span className={t.completed ? "text-emerald-700 line-through" : "text-slate-700"}>{t.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dealer Notes */}
          {visit.notes && (
            <div>
              <p className="text-sm font-medium text-navy mb-1">Dealer Notes</p>
              <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg whitespace-pre-wrap">{visit.notes}</p>
            </div>
          )}

          {/* Photos */}
          {visit.photos?.length > 0 && (
            <div>
              <p className="text-sm font-medium text-navy mb-2">Photos ({visit.photos.length})</p>
              <div className="grid grid-cols-3 gap-2">
                {visit.photos.map((p) => (
                  <div key={p.id} className="rounded-lg overflow-hidden border border-slate-200">
                    <img src={p.url} alt={p.caption || ""} className="w-full h-24 object-cover" />
                    {p.caption && <p className="text-xs text-slate-500 p-1.5">{p.caption}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Admin section */}
          <div className="border-t pt-4">
            <p className="text-sm font-medium text-navy mb-2">Admin Controls</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <Label className="text-xs">Status</Label>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full mt-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="visit-status-select">
                  <option value="scheduled">Scheduled</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="missed">Missed</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Admin Notes</Label>
              <Textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} rows={2} placeholder="Internal admin notes..." className="mt-1" data-testid="admin-notes" />
            </div>
            <div className="flex justify-end mt-3">
              <Button onClick={handleSave} disabled={saving} className="bg-teal hover:bg-teal/90 text-white" data-testid="save-visit-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Edit className="w-4 h-4 mr-2" />} Save Changes
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDealerVisits({ embedded = false }) {
  const [visits, setVisits] = useState([]);
  const [stats, setStats] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dealerFilter, setDealerFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [tab, setTab] = useState("overview");

  const fetchAll = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (dealerFilter) params.dealer_id = dealerFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [v, s, d, l] = await Promise.all([
        getDealerVisits(params),
        getDealerVisitStats(),
        getDealers(),
        getLocations(false),
      ]);
      setVisits(v);
      setStats(s);
      setDealers(d);
      setLocations(l);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [statusFilter, dealerFilter, dateFrom, dateTo]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredVisits = useMemo(() => {
    if (!search) return visits;
    const q = search.toLowerCase();
    return visits.filter((v) =>
      v.dealer_name?.toLowerCase().includes(q) ||
      v.location_name?.toLowerCase().includes(q) ||
      v.visit_type?.toLowerCase().includes(q)
    );
  }, [visits, search]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this visit?")) return;
    try {
      await deleteDealerVisit(id);
      toast.success("Visit deleted");
      fetchAll();
    } catch (err) { toast.error("Failed to delete"); }
  };

  return (
    <DealerVisitsLayout embedded={embedded}>
      <div className="space-y-6" data-testid="admin-dealer-visits">
        {embedded && (
          <Link to="/admin/reports" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-teal font-body" data-testid="back-to-reports">
            <ArrowLeft className="w-4 h-4" /> Back to Reports
          </Link>
        )}
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading font-bold text-2xl text-navy">Dealer Visits</h1>
            <p className="text-sm text-slate-500 font-body">Schedule, track, and manage dealer store visits</p>
          </div>
          <CreateVisitDialog dealers={dealers} locations={locations} onCreated={fetchAll} />
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="visit-stats">
            <StatCard label="Total Visits" value={stats.total} icon={ClipboardList} color="bg-slate-100 text-slate-600" />
            <StatCard label="Completed" value={stats.completed} icon={CheckCircle} color="bg-emerald-100 text-emerald-600" sub={`${stats.completion_rate}% rate`} />
            <StatCard label="Scheduled" value={stats.scheduled} icon={Calendar} color="bg-blue-100 text-blue-600" />
            <StatCard label="Today" value={stats.today} icon={Clock} color="bg-amber-100 text-amber-600" />
            <StatCard label="This Week" value={stats.week_completed} icon={TrendingUp} color="bg-indigo-100 text-indigo-600" sub="completed" />
          </div>
        )}

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-3 w-[420px]">
            <TabsTrigger value="overview">All Visits</TabsTrigger>
            <TabsTrigger value="calendar">Calendar View</TabsTrigger>
            <TabsTrigger value="repsly">Repsly Sync</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4" data-testid="visit-filters">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by dealer, location..." className="pl-10" data-testid="visit-search" />
              </div>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm w-36" data-testid="status-filter">
                <option value="all">All Status</option>
                <option value="scheduled">Scheduled</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="missed">Missed</option>
              </select>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36" data-testid="date-from" />
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36" data-testid="date-to" />
            </div>

            {/* Visit List */}
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-teal" /></div>
            ) : filteredVisits.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="py-12 text-center">
                  <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 font-body text-lg">No visits found</p>
                  <p className="text-slate-400 text-sm">Schedule a new visit to get started</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3" data-testid="visit-list">
                {filteredVisits.map((visit, i) => {
                  const cfg = STATUS_CONFIG[visit.status] || STATUS_CONFIG.scheduled;
                  const StatusIcon = cfg.icon;
                  const completedTasks = (visit.tasks || []).filter((t) => t.completed).length;
                  const totalTasks = (visit.tasks || []).length;
                  const visitType = VISIT_TYPES.find((t) => t.value === visit.visit_type);
                  return (
                    <motion.div key={visit.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                      <Card className="border-slate-200 hover:border-teal/20 transition-colors" data-testid={`visit-card-${visit.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.color}`}>
                              <StatusIcon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="font-heading font-semibold text-navy text-sm">{visit.location_name}</h3>
                                    <span className={`px-2 py-0.5 text-xs rounded font-medium ${cfg.color}`}>{cfg.label}</span>
                                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{visitType?.label || visit.visit_type}</span>
                                  </div>
                                  <p className="text-xs text-slate-500 mt-0.5">{visit.location_address}</p>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSelectedVisit(visit)} data-testid={`view-visit-${visit.id}`}>
                                    <Eye className="w-4 h-4 text-slate-400" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600" onClick={() => handleDelete(visit.id)} data-testid={`delete-visit-${visit.id}`}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </div>
                              <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {visit.dealer_name}</span>
                                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {visit.scheduled_date}</span>
                                {totalTasks > 0 && <span className="flex items-center gap-1"><ClipboardList className="w-3 h-3" /> {completedTasks}/{totalTasks} tasks</span>}
                                {visit.photos?.length > 0 && <span className="flex items-center gap-1"><Camera className="w-3 h-3" /> {visit.photos.length} photos</span>}
                                {visit.check_in_time && <span className="flex items-center gap-1"><Play className="w-3 h-3 text-amber-500" /> Checked in</span>}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="calendar" className="mt-4">
            <CalendarView visits={visits} onSelect={setSelectedVisit} />
          </TabsContent>

          <TabsContent value="repsly" className="mt-4">
            <RepslySettings />
          </TabsContent>
        </Tabs>

        {/* Detail Dialog */}
        <VisitDetailDialog visit={selectedVisit} open={!!selectedVisit} onClose={() => setSelectedVisit(null)} onUpdate={() => { fetchAll(); setSelectedVisit(null); }} />
      </div>
    </DealerVisitsLayout>
  );
}

function DealerVisitsLayout({ embedded, children }) {
  // Both modes use AdminLayout (sidebar must remain visible). The `embedded`
  // prop only changes the breadcrumb + adds a "Back to Reports" link.
  return (
    <AdminLayout
      title="Dealer Visits"
      breadcrumb={embedded ? "Reports / Dealer Visits" : "Dealer Visits"}
    >
      {children}
    </AdminLayout>
  );
}

function CalendarView({ visits, onSelect }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  const visitsByDay = {};
  visits.forEach((v) => {
    const d = v.scheduled_date?.slice(0, 10);
    if (!d) return;
    const date = new Date(d + "T12:00:00");
    if (date.getFullYear() === year && date.getMonth() === month) {
      const day = date.getDate();
      if (!visitsByDay[day]) visitsByDay[day] = [];
      visitsByDay[day].push(v);
    }
  });

  const today = new Date();
  const isToday = (d) => d && today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  return (
    <Card className="border-slate-200" data-testid="calendar-view">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <Button variant="outline" size="sm" onClick={() => setCurrentMonth(new Date(year, month - 1, 1))}>Prev</Button>
          <h3 className="font-heading font-bold text-navy">{monthNames[month]} {year}</h3>
          <Button variant="outline" size="sm" onClick={() => setCurrentMonth(new Date(year, month + 1, 1))}>Next</Button>
        </div>
        <div className="grid grid-cols-7 gap-1">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="text-center text-xs font-medium text-slate-400 py-2">{d}</div>
          ))}
          {days.map((day, i) => (
            <div key={i} className={`min-h-[70px] p-1 rounded-lg border text-xs ${day ? (isToday(day) ? "border-teal bg-teal/5" : "border-slate-100 hover:bg-slate-50") : "border-transparent"}`}>
              {day && (
                <>
                  <p className={`font-medium mb-0.5 ${isToday(day) ? "text-teal" : "text-slate-600"}`}>{day}</p>
                  {(visitsByDay[day] || []).slice(0, 2).map((v) => {
                    const cfg = STATUS_CONFIG[v.status] || STATUS_CONFIG.scheduled;
                    return (
                      <div key={v.id} className={`px-1 py-0.5 rounded text-[10px] mb-0.5 truncate cursor-pointer ${cfg.color}`} onClick={() => onSelect(v)} title={v.location_name}>
                        {v.location_name?.slice(0, 15)}
                      </div>
                    );
                  })}
                  {(visitsByDay[day] || []).length > 2 && (
                    <p className="text-[10px] text-slate-400">+{visitsByDay[day].length - 2} more</p>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}


function RepslySettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState({ api_key: "", api_passcode: "", enabled: false });

  useEffect(() => {
    getRepslySettings()
      .then((s) => { setSettings(s); setForm({ api_key: s.api_key || "", api_passcode: s.api_passcode || "", enabled: s.enabled || false }); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateRepslySettings(form);
      setSettings(updated);
      toast.success("Repsly settings saved");
    } catch (err) { toast.error("Failed to save settings"); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const result = await testRepslyConnection({ api_key: form.api_key, api_passcode: form.api_passcode });
      if (result.success) toast.success(result.message);
      else toast.error(result.message);
    } catch (err) { toast.error(err?.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await triggerRepslySync();
      toast.success(`Sync complete! ${result.visits_imported} visits imported, ${result.locations_pushed} locations pushed.`);
      if (result.errors?.length) toast.error(`${result.errors.length} errors during sync`);
      const updated = await getRepslySettings();
      setSettings(updated);
    } catch (err) { toast.error(err?.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-teal" /></div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="repsly-settings">
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-heading text-navy flex items-center gap-2"><Settings className="w-5 h-5" /> Repsly API Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-500">Connect your Repsly account for two-way visit sync. Find your API credentials in Repsly under Settings &gt; Integrations &gt; API.</p>
          <div>
            <Label>API Key</Label>
            <Input value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder="Your Repsly API key" data-testid="repsly-api-key" />
          </div>
          <div>
            <Label>API Passcode</Label>
            <Input type="password" value={form.api_passcode} onChange={(e) => setForm({ ...form, api_passcode: e.target.value })} placeholder="Your Repsly API passcode" data-testid="repsly-api-passcode" />
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="accent-teal" data-testid="repsly-enabled" />
              <span className="text-sm font-medium text-navy">Enable Repsly Integration</span>
            </label>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <Button onClick={handleSave} disabled={saving} className="bg-teal hover:bg-teal/90 text-white" data-testid="save-repsly-settings">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Settings className="w-4 h-4 mr-2" />} Save Settings
            </Button>
            <Button variant="outline" onClick={handleTest} disabled={testing || !form.api_key} data-testid="test-repsly-connection">
              {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wifi className="w-4 h-4 mr-2" />} Test Connection
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-heading text-navy flex items-center gap-2"><RefreshCw className="w-5 h-5" /> Sync</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="text-sm font-medium text-navy">Two-Way Sync</p>
              <p className="text-xs text-slate-500">Pull visits from Repsly &amp; push your locations to Repsly</p>
            </div>
            <Button onClick={handleSync} disabled={syncing || !form.api_key} className="bg-indigo-500 hover:bg-indigo-600 text-white" data-testid="trigger-sync-btn">
              {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />} Sync Now
            </Button>
          </div>
          {settings?.last_sync && (
            <p className="text-xs text-slate-400">Last sync: {new Date(settings.last_sync).toLocaleString()}</p>
          )}
          {settings?.sync_log?.length > 0 && (
            <div>
              <p className="text-sm font-medium text-navy mb-2">Sync History</p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {settings.sync_log.slice().reverse().map((log, i) => (
                  <div key={i} className="p-2 bg-slate-50 rounded-lg text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">{new Date(log.timestamp).toLocaleString()}</span>
                      <span className="text-slate-400">by {log.triggered_by}</span>
                    </div>
                    <p className="text-slate-700 mt-1">{log.visits_imported} visits imported, {log.locations_pushed} locations pushed</p>
                    {log.errors?.length > 0 && <p className="text-red-500 mt-1">{log.errors.length} errors</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
